import { useBoolean, useControllableProp } from "@design-system/hooks"
import {
  callAllHandlers,
  pick,
  scheduleMicrotask,
} from "@design-system/utils"
import { mergeRefs, PropGetter } from "@design-system/react-utils"
import {
  ChangeEvent,
  useCallback,
  useRef,
  useState,
} from "react"
import { useFormControl } from "@design-system/form-control"
import { RadioProps } from "./types"

export function useRadio(props: RadioProps = {}) {
  const {
    defaultChecked,
    isChecked: isCheckedProp,
    isFocusable,
    isDisabled,
    isReadOnly,
    isRequired,
    onChange,
    isInvalid,
    name,
    value,
    id,
    ...htmlProps
  } = props

  const [isFocused, setFocused] = useBoolean()

  const ref = useRef<HTMLInputElement>(null)

  const [isCheckedState, setChecked] = useState(Boolean(defaultChecked))

  const [isControlled, isChecked] = useControllableProp(
    isCheckedProp,
    isCheckedState,
  )

  const handleChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      if (isReadOnly || isDisabled) {
        event.preventDefault()
        return
      }

      if (!isControlled) {
        setChecked(event.target.checked)
      }

      onChange?.(event)
    },
    [isControlled, isDisabled, isReadOnly, onChange],
  )

  const inputProps = useFormControl<HTMLInputElement>(props)

  const getInputProps: PropGetter<HTMLInputElement> = useCallback(
    (props = {}, forwardedRef = null) => {
      const ownProps = pick(inputProps, [
        "id",
        "disabled",
        "readOnly",
        "required",
        "aria-invalid",
        "aria-required",
        "aria-readonly",
        "aria-describedby",
        "onFocus",
        "onBlur",
      ])

      /**
       * This is a workaround for React Concurrent Mode issue.
       * @see Issue https://github.com/facebook/react/issues/18591.
       *
       * Remove once it's fixed.
       */
      const focus = () => {
        scheduleMicrotask(() => {
          setFocused.on()
        })
      }

      const trulyDisabled = ownProps.disabled && !isFocusable

      return {
        ...props,
        ...ownProps,
        ref: mergeRefs(forwardedRef, ref),
        type: "radio",
        name,
        value,
        onChange: callAllHandlers(props.onChange, handleChange),
        onBlur: callAllHandlers(ownProps.onBlur, props.onBlur, setFocused.off),
        onFocus: callAllHandlers(ownProps.onFocus, props.onFocus, focus),
        checked: isChecked,
        disabled: trulyDisabled,
      }
    },
    [
      inputProps,
      isFocusable,
      name,
      value,
      handleChange,
      setFocused,
      isChecked,
    ],
  )

  return {
    state: {
      isInvalid,
      isFocused,
      isChecked,
      isDisabled,
      isReadOnly,
      isRequired,
    },
    getInputProps,
    htmlProps,
  }
}

export type UseRadioReturn = ReturnType<typeof useRadio>
